import {
    isOpenableLeadStatus,
    type PartnerCockpitOfferRequestDetail,
} from '@houzy/schemas/partner-cockpit/offer-requests';
import { PartnerEngagementStatus, type PostContactRequest } from '@houzy/schemas/partner-cockpit/partner-matching';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link, useCanGoBack, useLocation, useNavigate, useRouter } from '@tanstack/react-router';
import { useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import { FeedbackSnackbar } from '../../../components/FeedbackSnackbar';
import {
    useTrackEvent,
    type OfferRequestContactShortcut,
    type OfferRequestContactType,
    type OfferRequestList,
} from '../../../lib/trackingEvents';
import { AnnouncementBanner, useDismissBanner, useOfferRequestDetailBanners } from '../../announcements';
import { useDeclineMatching } from '../api/useDeclineMatching';
import { useMatchingEngagement, usePostContactRequest } from '../api/useMatchingEngagement';
import { useOfferRequestDetail } from '../api/useOfferRequestDetail';
import { useRecordOfferRequestOpening } from '../api/useRecordOfferRequestOpening';
import { useRevealContactDetail } from '../api/useRevealContactDetail';
import { useUpdateOfferRequestStatus } from '../api/useUpdateOfferRequestStatus';
import { DeclineOfferRequestDialog } from '../components/DeclineOfferRequestDialog';
import { OfferRequestChat } from '../components/OfferRequestChat';
import { OfferRequestChatUnavailableCard } from '../components/OfferRequestChatUnavailableCard';
import { OfferRequestContactPanel } from '../components/OfferRequestContactPanel';
import { OfferRequestContactScreen } from '../components/OfferRequestContactScreen';
import { OfferRequestDetailsCard } from '../components/OfferRequestDetailsCard';
import { OfferRequestStatusSelect } from '../components/OfferRequestStatusSelect';
import { ReimbursementButton } from '../components/ReimbursementButton';
import { FILL_HEIGHT_SECTION_SX } from '../fillHeight';
import { isOfferRequestStatusLocked, offerRequestCapabilities } from '../offerRequestCapabilities';
import { minutesSinceListed } from '../offerRequestTracking';
import type { ContactRequestValue, OfferRequestContactOption } from '../types';
import { toContactRequestPayload } from '../utils/contactRequestMapping';

const COLUMN_GAP = 24;

export type OfferRequestDetailPageProps = {
    offerRequestLeadUuid: string;
    // Below md the contact panel and chat move to their own screen, addressed by ?contact.
    isContactScreenOpen?: boolean;
    // Forwarded test/clock anchor so stories and tests stay deterministic.
    now?: Date;
};

export function OfferRequestDetailPage({
    offerRequestLeadUuid,
    isContactScreenOpen = false,
    now,
}: OfferRequestDetailPageProps): ReactElement {
    const { t } = useTranslation();
    const router = useRouter();
    const navigate = useNavigate();
    const canGoBack = useCanGoBack();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const query = useOfferRequestDetail(offerRequestLeadUuid);
    const pathname = useLocation({ select: (location) => location.pathname });
    const list: OfferRequestList = pathname.startsWith('/open-requests') ? 'open' : 'new';
    useRecordOfferRequestOpening(offerRequestLeadUuid, query.data, list);
    const { data: offerRequestDetailBanners } = useOfferRequestDetailBanners();
    const dismissBanner = useDismissBanner();
    const updateStatus = useUpdateOfferRequestStatus(list);
    const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
    const [phoneEmailContactLeadUuid, setPhoneEmailContactLeadUuid] = useState<string>();
    // The lead's own response says whether it is a matching, so this stays false until the detail has loaded.
    const { canEngageCustomer } = offerRequestCapabilities(query.data?.source);
    const matchingUuid = canEngageCustomer ? offerRequestLeadUuid : null;
    const engagementQuery = useMatchingEngagement(matchingUuid);
    const postContactRequest = usePostContactRequest(matchingUuid);
    const declineMatching = useDeclineMatching();
    const revealContactDetail = useRevealContactDetail(offerRequestLeadUuid);
    const track = useTrackEvent();
    // A shortcut can be clicked again and again; the first click per lead in a visit is the contact. Keyed by lead
    // because moving to another lead keeps this page mounted.
    const trackedShortcuts = useRef(new Set<string>());

    const engagement = engagementQuery.data ?? null;
    const isResolvingEngagement = canEngageCustomer && !engagementQuery.isFetched;
    // A lead with no engagement to make shows the (unavailable) chat card, never a contact form.
    const hasChat = !canEngageCustomer || (engagement !== null && engagement.contactRequest !== null);

    function contactRequestPayload(option: OfferRequestContactOption, value?: ContactRequestValue): PostContactRequest {
        return toContactRequestPayload(option, value, {
            taxed: {
                netPrice: t('OFFER_REQUESTS.CONTACT.REQUEST.NET_PRICE'),
                vatRate: t('OFFER_REQUESTS.CONTACT.REQUEST.VAT_RATE'),
                grossPrice: t('OFFER_REQUESTS.CONTACT.REQUEST.GROSS_PRICE'),
            },
            untaxed: {
                price: t('OFFER_REQUESTS.CONTACT.REQUEST.PRICE'),
            },
        });
    }

    function trackContact(contactType: OfferRequestContactType): void {
        if (query.data) {
            track('Offer Request Contact Initialised', {
                offerRequestId: query.data.referenceId,
                minutesSinceListed: minutesSinceListed(query.data.matchedAt),
                contactType,
            });
        }
    }

    function submitContactRequest(option: OfferRequestContactOption, value?: ContactRequestValue): void {
        const payload = contactRequestPayload(option, value);
        postContactRequest.mutate(payload, { onSuccess: () => trackContact(payload.type) });
    }

    function trackContactShortcut(contactType: OfferRequestContactShortcut): void {
        const shortcut = `${offerRequestLeadUuid}:${contactType}`;
        if (trackedShortcuts.current.has(shortcut)) {
            return;
        }
        trackedShortcuts.current.add(shortcut);
        trackContact(contactType);
    }

    function handleConfirmDecline(): void {
        if (declineMatching.isPending || !canEngageCustomer || !query.data) {
            return;
        }
        declineMatching.mutate(query.data, { onSuccess: () => setDeclineDialogOpen(false) });
    }

    // A lead nobody can act on has no one to talk to, and no contact details that are still ours to show.
    function engagementSection(data: PartnerCockpitOfferRequestDetail, fillHeight: boolean): ReactElement | undefined {
        if (!isOpenableLeadStatus(data.leadStatus)) {
            return undefined;
        }

        const sectionSx = fillHeight ? FILL_HEIGHT_SECTION_SX : undefined;

        if (!canEngageCustomer) {
            return (
                <Box component="section" aria-label={t('OFFER_REQUESTS.CONTACT.SECTION')} sx={sectionSx}>
                    <OfferRequestChatUnavailableCard
                        contact={data.contact}
                        leadStatus={data.leadStatus}
                        contactRestricted={data.contactRestricted}
                        onRevealContactDetail={revealContactDetail}
                        fillHeight={fillHeight}
                        onContactShortcut={trackContactShortcut}
                    />
                </Box>
            );
        }

        if (isResolvingEngagement) {
            return (
                <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320, py: 6, ...sectionSx }}>
                    <CircularProgress aria-label={t('OFFER_REQUESTS.LOADING')} />
                </Stack>
            );
        }

        if (
            engagement !== null &&
            engagement.contactRequest !== null &&
            phoneEmailContactLeadUuid !== offerRequestLeadUuid
        ) {
            return (
                <Box component="section" aria-label={t('OFFER_REQUESTS.CHAT.SECTION')} sx={sectionSx}>
                    <OfferRequestChat
                        key={offerRequestLeadUuid}
                        engagementUuid={engagement.uuid}
                        offerRequestId={data.referenceId}
                        contact={data.contact}
                        leadStatus={data.leadStatus}
                        contactRestricted={data.contactRestricted}
                        onRevealContactDetail={revealContactDetail}
                        isInputDisabled={engagement.status !== PartnerEngagementStatus.Accepted}
                        isOnOldContract={data.isOnOldContract}
                        fillHeight={fillHeight}
                    />
                </Box>
            );
        }

        return (
            <Box component="section" aria-label={t('OFFER_REQUESTS.CONTACT.SECTION')} sx={sectionSx}>
                <OfferRequestContactPanel
                    contact={data.contact}
                    leadStatus={data.leadStatus}
                    contactRestricted={data.contactRestricted}
                    onRevealContactDetail={revealContactDetail}
                    isOnOldContract={data.isOnOldContract}
                    fillHeight={fillHeight}
                    submitDisabled={postContactRequest.isPending}
                    onSubmitRequest={(option, value) => submitContactRequest(option, value)}
                    onConfirmInterest={(option) => submitContactRequest(option)}
                    onConfirmPhoneEmailContact={(option) => {
                        const payload = contactRequestPayload(option);
                        setPhoneEmailContactLeadUuid(offerRequestLeadUuid);
                        return postContactRequest
                            .mutateAsync(payload, { onSuccess: () => trackContact(payload.type) })
                            .catch((error: unknown) => {
                                setPhoneEmailContactLeadUuid(undefined);
                                throw error;
                            });
                    }}
                    onPhoneEmailContactCompleted={() => setPhoneEmailContactLeadUuid(undefined)}
                />
                {postContactRequest.isError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {t('OFFER_REQUESTS.CONTACT.REQUEST_ERROR')}
                    </Alert>
                )}
            </Box>
        );
    }

    // Return to wherever the user came from and, on a hard load with no in-app history, fall back to the list this
    // detail is nested under.
    const listFallbackPath = list === 'open' ? '/open-requests' : '/new-requests';
    const backButton = canGoBack ? (
        <Button onClick={() => router.history.back()} startIcon={<ChevronLeftIcon />} color="inherit">
            {t('OFFER_REQUESTS.DETAIL.BACK')}
        </Button>
    ) : (
        <Button component={Link} to={listFallbackPath} startIcon={<ChevronLeftIcon />} color="inherit">
            {t('OFFER_REQUESTS.DETAIL.BACK')}
        </Button>
    );

    // Built once and placed by breakpoint: desktop keeps all three in one right-hand cluster, a phone
    // keeps the status beside the back button and drops the other two to their own row.
    const detail = query.status === 'success' ? query.data : undefined;
    const isActionable = detail !== undefined && isOpenableLeadStatus(detail.leadStatus);

    const statusControl = isActionable ? (
        <OfferRequestStatusSelect
            status={detail.status}
            disabled={isOfferRequestStatusLocked(detail)}
            onStatusChange={(status) => updateStatus.mutate({ uuid: offerRequestLeadUuid, status })}
        />
    ) : undefined;

    const reimbursementControl = detail?.reimbursement ? (
        <ReimbursementButton
            state={detail.reimbursement}
            reimbursedCredits={detail.reimbursedCredits}
            offerRequestLeadUuid={offerRequestLeadUuid}
            origin={pathname.startsWith('/open-requests') ? 'open-requests' : 'new-requests'}
        />
    ) : undefined;

    const declineControl =
        isActionable && detail.canDecline ? (
            <Button
                startIcon={<HighlightOffIcon />}
                color="error"
                disabled={declineMatching.isPending}
                onClick={() => setDeclineDialogOpen(true)}>
                {t('OFFER_REQUESTS.DETAIL.DECLINE')}
            </Button>
        ) : undefined;

    // On a phone the panel gets the whole viewport, so mount it only while that screen is open — a
    // second mount behind the detail would poll the chat and duplicate the composer.
    const mobileContactSection =
        isMobile && isContactScreenOpen && query.data !== undefined ? engagementSection(query.data, true) : undefined;

    function closeContactScreen(): void {
        if (canGoBack) {
            router.history.back();
            return;
        }
        void navigate({ to: '.', search: {}, replace: true });
    }

    if (mobileContactSection !== undefined) {
        return (
            <OfferRequestContactScreen onBack={closeContactScreen}>{mobileContactSection}</OfferRequestContactScreen>
        );
    }

    return (
        <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 } }}>
            {offerRequestDetailBanners && offerRequestDetailBanners.length > 0 ? (
                <Stack spacing={2} sx={{ pt: 3 }}>
                    {offerRequestDetailBanners.map((banner) => (
                        <AnnouncementBanner
                            key={banner.uuid}
                            banner={banner}
                            onDismiss={() => dismissBanner.mutate(banner.uuid)}
                        />
                    ))}
                </Stack>
            ) : undefined}

            <Box sx={{ py: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Box sx={{ mr: 'auto' }}>{backButton}</Box>
                {isMobile ? (
                    <>
                        {statusControl}
                        {reimbursementControl !== undefined || declineControl !== undefined ? (
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ flexBasis: '100%', justifyContent: 'flex-end' }}>
                                {reimbursementControl}
                                {declineControl}
                            </Stack>
                        ) : undefined}
                    </>
                ) : (
                    <Stack direction="row" spacing={1} alignItems="center">
                        {reimbursementControl}
                        {statusControl}
                        {declineControl}
                    </Stack>
                )}
            </Box>

            {match(query)
                .with({ status: 'pending' }, () => (
                    <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress aria-label={t('OFFER_REQUESTS.LOADING')} />
                    </Stack>
                ))
                .with({ status: 'error' }, () => <Alert severity="error">{t('OFFER_REQUESTS.ERROR.LOAD')}</Alert>)
                .with({ status: 'success' }, ({ data }) => {
                    const contactColumn = isMobile ? undefined : engagementSection(data, false);
                    return (
                        <>
                            {isMobile && isOpenableLeadStatus(data.leadStatus) ? (
                                <Button
                                    variant="contained"
                                    fullWidth
                                    // The engagement is not prefetched, so waiting here beats a label
                                    // that flips from "Contact customer" to "Chat" a moment after paint.
                                    loading={isResolvingEngagement}
                                    sx={{ mb: 2 }}
                                    onClick={() => void navigate({ to: '.', search: { contact: true } })}>
                                    {t(
                                        hasChat
                                            ? 'OFFER_REQUESTS.DETAIL.OPEN_CHAT'
                                            : 'OFFER_REQUESTS.DETAIL.CONTACT_CUSTOMER',
                                    )}
                                </Button>
                            ) : undefined}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: contactColumn ? { xs: '1fr', md: '1fr 1fr' } : '1fr',
                                    gap: `${COLUMN_GAP}px`,
                                    alignItems: 'start',
                                }}>
                                <Box component="section" aria-label={t('OFFER_REQUESTS.DETAIL.SECTION')}>
                                    <OfferRequestDetailsCard offerRequest={data} now={now} />
                                </Box>
                                {contactColumn}
                            </Box>
                        </>
                    );
                })
                .exhaustive()}

            {query.data?.canDecline === true ? (
                <DeclineOfferRequestDialog
                    open={declineDialogOpen}
                    onConfirm={handleConfirmDecline}
                    onCancel={() => setDeclineDialogOpen(false)}
                    confirmDisabled={declineMatching.isPending}
                />
            ) : undefined}

            <FeedbackSnackbar
                open={updateStatus.isError}
                severity="error"
                message={t('OPEN_REQUESTS.STATUS_UPDATE_ERROR')}
                onClose={() => updateStatus.reset()}
            />

            <FeedbackSnackbar
                open={declineMatching.isError}
                severity="error"
                message={t('OFFER_REQUESTS.DETAIL.DECLINE_ERROR')}
                onClose={() => declineMatching.reset()}
            />
        </Box>
    );
}
